import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createScenario,
  deleteScenario,
  fetchDetailScenario,
  fetchScenarioList,
  updateScenario,
} from '@/domains/scenario/apis/scenario-management';
import type {
  CreateScenarioRequest,
  ScenarioDetailResponse,
  ScenarioInputForm,
  ScenarioListResponse,
  UpdateScenarioRequest,
} from '@/domains/scenario/types/scenario-management.types';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

// Hook parameter types
interface UseScenarioManagementParams {
  scenarioId?: string;
  queryParams?: string;
  onCreateSuccess?: (data: ScenarioInputForm) => void;
  onUpdateSuccess?: (data: ScenarioDetailResponse) => void;
  onDeleteSuccess?: () => void;
}

// Hook return type
interface UseScenarioManagementReturn {
  scenarioListData: ScenarioListResponse | undefined;
  detailScenarioData: ScenarioDetailResponse | undefined;
  deleteScenario: (scenarioId: string) => void;
  createScenario: (scenarioData: CreateScenarioRequest) => void;
  updateScenario: (scenarioData: UpdateScenarioRequest) => void;
}

const useScenarioManagement = ({
  scenarioId = '',
  queryParams = '',
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
  onDeleteSuccess = () => {},
}: UseScenarioManagementParams): UseScenarioManagementReturn => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Query to fetch scenario list
  const {
    data: scenarioListData,
    // error: scenarioListError,
    // isLoading: isLoadingScenario,
  } = useQuery({
    queryKey: ['scenarioListData', queryParams],
    queryFn: () => fetchScenarioList(queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  // Query to fetch detailed scenario data
  const {
    data: detailScenarioData,
    // error: detailScenarioError,
    // isLoading: isLoadingDetailScenario,
  } = useQuery({
    queryKey: ['detailScenarioData', scenarioId],
    queryFn: () => fetchDetailScenario(scenarioId),
    retry: false,
    enabled: !!scenarioId,
  });

  const deleteScenarioMutation = useMutation({
    mutationFn: (scenarioId: string) => deleteScenario(scenarioId),
    onSuccess: (_responseData) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 삭제되었습니다.'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({
            queryKey: ['scenarioListData', queryParams],
          });
        },
      });
    },
    onError: (err: Error) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // Mutation to create a Scenario (POST request)
  const createScenarioMutation = useMutation({
    mutationFn: (scenarioData: CreateScenarioRequest) => createScenario(scenarioData),
    onSuccess: (responseData: ScenarioDetailResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['scenarioListData', queryParams],
          });
          queryClient.invalidateQueries({
            queryKey: ['detailScenarioData', scenarioId],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (err: Error) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  const updateScenarioMutation = useMutation({
    mutationFn: (scenarioData: UpdateScenarioRequest) => updateScenario(scenarioData),
    onSuccess: (responseData: ScenarioDetailResponse) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('정상적으로 정보가 입력되었습니다.'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['scenarioListData', queryParams],
          });
          queryClient.invalidateQueries({
            queryKey: ['detailScenarioData', scenarioId],
          });
          onUpdateSuccess(responseData);
        },
      });
    },
    onError: (err: Error) => {
      console.error('Error updating user:', err);
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  return {
    scenarioListData,
    detailScenarioData,
    deleteScenario: deleteScenarioMutation.mutate,
    createScenario: createScenarioMutation.mutate,
    updateScenario: updateScenarioMutation.mutate,
  };
};

export default useScenarioManagement;
